import { ERoles } from '@constants';
import { IFile, IBasicUser, ICreateComment } from '@sdk/contracts';
import { Button, Form, Popconfirm } from 'antd';
import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CreateCommentForm } from '../CreateCommentForm';

import {
  ActionsWrapper,
  CommentDivider,
  DateWrapper,
  FooterWrapper,
  SeeMoreWrapper,
  ContentHeaderWrapper,
  ContentWrapper,
  FileWrapper,
  IssuerWrapper,
  UserDetailWrapper,
  AnswerWrapper,
  DescriptionWrapper
} from './CommentContent.styles';

import { Icon } from '@/components/Icon';
import { usePublicProfileViewer } from '@/hooks';
import { getCreatedAtDateString } from '@/pages/Student/Course/utils';
import { useMutateEvents, useProfile } from '@/store';

type Props = {
  commentId: string;
  content?: string;
  sender: IBasicUser;
  file?: IFile;
  formula?: string;
  createdAt: Date;
  canAnswer?: boolean;
  onDeleteMessage?: () => void;
  isHighlighted?: boolean;
  canDelete?: boolean;
};

const ATTACHMENT_ICON_SIZE = 12;

// TODO: Add report comment button
export const CommentContent: React.FC<Props> = ({
  commentId,
  content,
  sender,
  file,
  formula,
  createdAt,
  canAnswer = false,
  onDeleteMessage,
  isHighlighted = false,
  canDelete = true
}) => {
  const { t } = useTranslation('StudentCourseTranslations');
  const [isExpanded, setIsExpanded] = useState(false);
  const [isAnswerFormVisible, setIsAnswerFormVisible] = useState(false);
  const {
    profile,
    roleCheck: { isManager }
  } = useProfile();
  const { sendMessage } = useMutateEvents();
  const toggleExpanded = () => setIsExpanded(!isExpanded);
  const contentHeaderRef = useRef<HTMLDivElement>(null);
  const { setUserId } = usePublicProfileViewer();
  const [form] = Form.useForm();
  const isStudent = useMemo(() => sender.role === ERoles.STUDENT, [sender]);

  const isMyMessage = useMemo(() => {
    return profile?._id === sender._id;
  }, [profile, sender]);

  const onAnswerSubmit = (values: ICreateComment) => {
    if (!commentId || !values.content) return;
    sendMessage(
      {
        commentId,
        message: values.content,
        attached: values.file
      },
      { onSuccess: onAnswerCreated }
    );
  };

  const onAnswerCreated = () => {
    form.resetFields();
    setIsAnswerFormVisible(false);
  };

  const checkContainerHeight = () => {
    if (contentHeaderRef.current) {
      const containerHeight = contentHeaderRef.current.clientHeight;
      return containerHeight >= 260;
    }
    return false;
  };

  const handleOpenFile = () => window.open(file?.url, '_blank');

  const isContentTooLong = checkContainerHeight();

  const handlePreviewUser = () => {
    if (!isStudent) return;
    setUserId(sender._id);
  };
  const canDeleteComment = (isMyMessage && canDelete) || isManager;
  return (
    <ContentWrapper>
      <ContentHeaderWrapper
        ref={contentHeaderRef}
        $isExpanded={isExpanded}
        $isHighlighted={isHighlighted}>
        <UserDetailWrapper onClick={handlePreviewUser} $canHover={isStudent}>
          <IssuerWrapper>{`${sender.name} ${sender.lastName}`}</IssuerWrapper>
          <DateWrapper>{`${getCreatedAtDateString(createdAt, t)} · ${
            isStudent ? t('STUDENT') : t('TEACHER')
          }`}</DateWrapper>
        </UserDetailWrapper>
        {file && (
          <FileWrapper onClick={handleOpenFile}>
            <Icon size={ATTACHMENT_ICON_SIZE} i="Attachment" />
            <div>{file.name}</div>
          </FileWrapper>
        )}
        {content && <DescriptionWrapper dangerouslySetInnerHTML={{ __html: content }} />}
        {formula && <div dangerouslySetInnerHTML={{ __html: formula }} />}
        {!isExpanded && isContentTooLong && <CommentDivider />}
      </ContentHeaderWrapper>
      <FooterWrapper>
        {isContentTooLong && (
          <SeeMoreWrapper onClick={toggleExpanded}>
            <div>{isExpanded ? t('SEE_LESS') : t('SEE_MORE')}</div>
            <Icon i={isExpanded ? 'ChevronUp' : 'ChevronDown'} />
          </SeeMoreWrapper>
        )}
        <ActionsWrapper>
          {canDeleteComment && (
            <Popconfirm title={t('DELETE_MESSAGE_CONFIRMATION')} onConfirm={onDeleteMessage}>
              <Button type="link" danger shape="circle" size="small" icon={<Icon i="TrashCan" />} />
            </Popconfirm>
          )}
        </ActionsWrapper>
      </FooterWrapper>
      {canAnswer && (
        <>
          {isAnswerFormVisible ? (
            <CreateCommentForm
              form={form}
              isActive={isAnswerFormVisible}
              setIsActive={setIsAnswerFormVisible}
              alwaysExpanded
              onFormSubmit={onAnswerSubmit}
              onCancel={() => setIsAnswerFormVisible(false)}
            />
          ) : (
            <AnswerWrapper onClick={() => setIsAnswerFormVisible(true)}>
              <Icon i="AddComment" />
              <div>{t('ANSWER')}</div>
            </AnswerWrapper>
          )}
        </>
      )}
    </ContentWrapper>
  );
};
